/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 06/10/23
 */
import React, { ChangeEvent } from 'react';
import {
    Button,
    FormLabel,
    FormLabelProps,
    Input,
    InputGroup,
    InputProps,
    InputRightElement,
    SimpleGrid,
} from '@chakra-ui/react';

interface FormDateInputProps {
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    value: string | null;
    fieldName: string;
    isReadonly: boolean;
    label: string;
    inputProps?: InputProps;
    onFocus?: () => void;
    max?: string;
    min?: string;
    formLabelProps?: FormLabelProps;
}

export const FormDateInput: React.FC<FormDateInputProps> = ({
    handleChange,
    value,
    fieldName,
    isReadonly,
    label,
    onFocus,
    max,
    min,
    formLabelProps,
}) => {
    const handleClear = () => {
        handleChange({
            target: {
                name: fieldName,
                value: '',
            } as HTMLInputElement,
        } as ChangeEvent<HTMLInputElement>);
    };

    return (
        <SimpleGrid templateColumns="0.2fr 0.25fr" alignItems="center" my={4} gridColumnGap={3}>
            <FormLabel minWidth="70px" htmlFor={fieldName} textColor="blue.500" {...formLabelProps}>
                {label}:
            </FormLabel>
            <InputGroup maxWidth="fit-content">
                <Input
                    id={fieldName}
                    name={fieldName}
                    type="date"
                    onChange={(e) => handleChange(e)}
                    value={value ?? undefined}
                    isReadOnly={isReadonly}
                    onFocus={onFocus}
                    aria-label={value ?? ''}
                    maxWidth="sm"
                    pr="7rem"
                    max={max}
                    min={min}
                />
                <InputRightElement w="auto">
                    {!isReadonly && value && (
                        <Button
                            size="sm"
                            onClick={() => handleClear()}
                            aria-label={`Clear ${label} Date`}
                            onPointerDown={onFocus}
                        >
                            Clear
                        </Button>
                    )}
                </InputRightElement>
            </InputGroup>
        </SimpleGrid>
    );
};
